"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";

function checkVisibility(el: Element) {
  const style = window.getComputedStyle(el);
  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    style.opacity !== "0"
  );
}

export default function InfiniteMoving({
  direction = "left",
  speed = "normal",
  gap = "none",
  pauseOnHover = true,
  mask,
  className,
  children,
  disabled,
}: {
  direction?: "left" | "right";
  speed?: "fast" | "normal" | "slow";
  /**
   * `sm: 8px; md: 16px; lg: 24px; xl: 32px; 2xl: 48px; 3xl: 64px; 4xl: 96px;`
   * @default none
   */
  gap?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  pauseOnHover?: boolean;
  mask?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!disabled) {
      addAnimation();
    }
  }, [disabled]);

  const [start, setStart] = useState(false);

  function addAnimation() {
    if (
      containerRef.current &&
      scrollerRef.current &&
      checkVisibility(scrollerRef.current)
    ) {
      const scrollerContent = Array.from(scrollerRef.current.children);

      scrollerContent.forEach((item) => {
        const duplicatedItem = item.cloneNode(true);
        if (scrollerRef.current) {
          scrollerRef.current.appendChild(duplicatedItem);
        }
      });

      getDirection();
      getSpeed();
      setStart(true);
    }
  }
  const getDirection = () => {
    if (containerRef.current) {
      if (direction === "left") {
        containerRef.current.style.setProperty(
          "--animation-direction",
          "forwards"
        );
      } else {
        containerRef.current.style.setProperty(
          "--animation-direction",
          "reverse"
        );
      }
    }
  };
  const getSpeed = () => {
    if (containerRef.current) {
      if (speed === "fast") {
        containerRef.current.style.setProperty("--animation-duration", "20s");
      } else if (speed === "normal") {
        containerRef.current.style.setProperty("--animation-duration", "40s");
      } else {
        containerRef.current.style.setProperty("--animation-duration", "80s");
      }
    }
  };
  return (
    <div
      ref={containerRef}
      className={cn(
        "scroller relative z-20 overflow-x-hidden",
        mask &&
          "[mask-image:linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]",
        className
      )}>
      <div
        ref={scrollerRef}
        className={cn(
          "flex w-max min-w-full  shrink-0 flex-nowrap",
          start && "animate-scroll ",
          pauseOnHover && "hover:[animation-play-state:paused]",
          gap === "sm" && "gap-x-2",
          gap === "md" && "gap-x-4",
          gap === "lg" && "gap-x-6",
          gap === "xl" && "gap-x-8",
          gap === "2xl" && "gap-x-12",
          gap === "3xl" && "gap-x-16",
          gap === "4xl" && "gap-x-24"
        )}>
        {children}
      </div>
    </div>
  );
}
